<div class="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-5 py-7 sm:px-8 sm:pt-12">
  <div class="flex flex-col gap-3">
    <p class="m-0 text-sm font-bold uppercase tracking-wide text-primary">Quick Brief</p>
    <h1 class="use-grotesque m-0 text-4xl font-bold leading-none tracking-tight text-balance sm:text-5xl">
      What are we <span class="text-primary">promoting?</span>
    </h1>
    <p class="m-0 text-xl font-semibold leading-snug text-muted-foreground">
      Answer three quick questions. SHOUT AI writes the rest of your brief from {{ brief.domain() || 'your site' }}.
    </p>
  </div>

  <div class="flex flex-col gap-3" role="group" aria-label="What are we promoting?">
    <button type="button" class="flex w-full items-center gap-4 rounded-xl p-4 text-left" [class]="optionClass(brief.focus() === 'brand')" [attr.aria-pressed]="brief.focus() === 'brand'" (click)="brief.selectFocus('brand')">
      <span class="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="size-6">
          <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18" />
          <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
          <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
          <path d="M10 6h4" /><path d="M10 10h4" /><path d="M10 14h4" /><path d="M10 18h4" />
        </svg>
      </span>
      <span class="min-w-0 flex-1">
        <span class="block text-xl font-bold">My brand</span>
        <span class="block text-base font-medium text-muted-foreground">Build awareness for {{ brief.brandName() }} as a whole.</span>
      </span>
      <span class="flex size-6 shrink-0 items-center justify-center rounded-full border-2" [class]="brief.focus() === 'brand' ? 'border-primary' : 'border-muted-foreground'" aria-hidden="true">
        @if (brief.focus() === 'brand') {
          <span class="size-3 rounded-full bg-primary"></span>
        }
      </span>
    </button>

    <button type="button" class="flex w-full items-center gap-4 rounded-xl p-4 text-left" [class]="optionClass(brief.focus() === 'product')" [attr.aria-pressed]="brief.focus() === 'product'" (click)="brief.selectFocus('product')">
      <span class="flex size-12 shrink-0 items-center justify-center rounded-lg bg-brand-studio text-white" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="size-6">
          <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
          <path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" />
        </svg>
      </span>
      <span class="min-w-0 flex-1">
        <span class="block text-xl font-bold">A specific product</span>
        <span class="block text-base font-medium text-muted-foreground">Put one product in front of new customers.</span>
      </span>
      <span class="flex size-6 shrink-0 items-center justify-center rounded-full border-2" [class]="brief.focus() === 'product' ? 'border-primary' : 'border-muted-foreground'" aria-hidden="true">
        @if (brief.focus() === 'product') {
          <span class="size-3 rounded-full bg-primary"></span>
        }
      </span>
    </button>
  </div>

  @if (brief.focus() === 'product') {
    @if (brief.product(); as product) {
      <div class="flex flex-col gap-2">
        <p class="m-0 text-base font-bold">Product</p>
        <div class="rounded-xl border border-border bg-card p-4 shadow-md sm:p-5">
          <div class="flex gap-4">
            <img [src]="brief.imageSrc(product.imageUrl)" [alt]="product.name" class="size-24 shrink-0 rounded-lg bg-muted object-cover sm:size-28" />
            <div class="flex min-w-0 flex-1 flex-col gap-2">
              <div class="flex items-start justify-between gap-3">
                <p class="m-0 min-w-0 text-lg font-bold leading-tight">{{ product.name }}</p>
                @if (product.price) {
                  <p class="m-0 shrink-0 text-lg font-extrabold">{{ product.price }} {{ product.currency }}</p>
                }
              </div>
              <p class="m-0 line-clamp-3 text-sm font-medium leading-relaxed text-muted-foreground">{{ product.description }}</p>
              <div class="mt-auto flex items-center justify-between gap-3 border-t border-border pt-3">
                <p class="m-0 min-w-0 flex-1 truncate text-sm font-medium text-muted-foreground" [title]="product.sourceUrl">{{ product.sourceUrl }}</p>
                <button type="button" class="btn-shout btn-shout-xs btn-shout-tertiary shrink-0" (click)="brief.changeProduct()">Change</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    } @else {
      <div class="flex flex-col gap-2">
        <label for="product-url" class="text-base font-bold">Product page URL</label>
        <div class="flex items-center gap-2 rounded-lg border border-border bg-card px-3 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
          <input id="product-url" type="url" inputmode="url" autocomplete="url" autocapitalize="none" class="h-14 min-w-0 flex-1 bg-transparent text-lg font-medium outline-none" [value]="brief.productUrl()" [placeholder]="(brief.domain() || 'yourbrand.com') + '/products/…'" [disabled]="brief.productLoading()" (input)="onUrl($event)" (keydown.enter)="brief.fetchProduct()" />
          <button type="button" class="btn-shout btn-shout-sm btn-shout-primary shrink-0" [disabled]="brief.productLoading()" (click)="brief.fetchProduct()">
            {{ brief.productLoading() ? 'Fetching…' : 'Fetch' }}
          </button>
        </div>
        @if (brief.productLoading()) {
          <div class="flex items-center gap-3 rounded-xl border border-border p-4" aria-live="polite">
            <div class="size-20 animate-pulse rounded-lg bg-muted"></div>
            <p class="m-0 text-base font-bold text-primary">Fetching product details…</p>
          </div>
        }
      </div>

      @if (brief.manualEntry()) {
        <div class="flex flex-col gap-3 rounded-xl border border-border bg-card p-4">
          <p class="m-0 text-base font-bold">Enter the product details</p>
          <label class="flex flex-col gap-1 text-sm font-bold" for="manual-name">
            Name
            <input id="manual-name" type="text" class="h-12 rounded-lg border border-border bg-background px-3 text-base font-medium" [value]="brief.manual().name" (input)="onManual('name', $event)" />
          </label>
          <label class="flex flex-col gap-1 text-sm font-bold" for="manual-description">
            Description
            <textarea id="manual-description" rows="3" class="rounded-lg border border-border bg-background px-3 py-2 text-base font-medium" [value]="brief.manual().description" (input)="onManual('description', $event)"></textarea>
          </label>
          <div class="grid grid-cols-2 gap-3">
            <label class="flex flex-col gap-1 text-sm font-bold" for="manual-price">
              Price
              <input id="manual-price" type="text" inputmode="decimal" class="h-12 rounded-lg border border-border bg-background px-3 text-base font-medium" [value]="brief.manual().price" (input)="onManual('price', $event)" />
            </label>
            <label class="flex flex-col gap-1 text-sm font-bold" for="manual-currency">
              Currency
              <select id="manual-currency" class="h-12 rounded-lg border border-border bg-background px-3 text-base font-medium" [value]="brief.manual().currency" (change)="onManual('currency', $event)">
                <option value="">Choose</option>
                <option value="GBP">GBP</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </label>
          </div>
          <div class="flex flex-col gap-1">
            <p class="m-0 text-sm font-bold">Image</p>
            <atom-add-image [initialImage]="brief.manual().imageUrl" [bucket]="bucket" size="sm" (imageUploaded)="onImage($event)" />
          </div>
        </div>
      }
    }
  }

  @if (brief.error()) {
    <p class="m-0 text-base font-semibold text-red-600" role="alert">{{ brief.error() }}</p>
  }

</div>
<div class="shrink-0 border-t border-border bg-background px-5 py-3 sm:px-8">
  <button type="button" class="btn-shout btn-shout-md btn-shout-primary w-full" [disabled]="brief.productLoading()" (click)="brief.continueFromFocus()">Continue</button>
</div>
